import type { Exercise } from "./schemas/exercise.js";
import type { PlannedExerciseEntry } from "./schemas/plan.js";
import type { ProgramSession } from "./schemas/program.js";
import type { ProgramModel } from "./programModel.js";

// THE "which exercises / primary muscles does this program/plan touch"
// derivation — session → slot → variant → exerciseId (resp. entry →
// exerciseId), then slug → catalog.primary. Extracted from the inline
// useMemo loops in ProgramPreview / PlanPreview when the Library
// tag-filter became the third live caller (HR-S3c). Takes the
// sessions/entries arrays (not the whole Program/Plan) so locale-resolved
// templates satisfy the signature too. Returns UNSORTED arrays — sorting
// by localized name (muscleName + localeCompare) stays a UI concern.

export const programExerciseSlugs = (
  sessions: ReadonlyArray<ProgramSession>,
): string[] => {
  const set = new Set<string>();
  for (const session of sessions) {
    for (const slot of session.slots) {
      for (const variant of slot.variants) set.add(variant.exerciseId);
    }
  }
  return Array.from(set);
};

export const planExerciseSlugs = (
  entries: ReadonlyArray<PlannedExerciseEntry>,
): string[] => {
  const set = new Set<string>();
  for (const entry of entries) set.add(entry.exerciseId);
  return Array.from(set);
};

/** #472: a model's default exercises, every level and focus. */
export const modelExerciseSlugs = (
  model: Pick<ProgramModel, "days">,
): string[] => {
  const set = new Set<string>();
  for (const day of model.days) {
    for (const row of day.rows) {
      const choices = [
        ...Object.values(row.byLevel),
        ...Object.values(row.byFocus ?? {}).flatMap((l) => Object.values(l ?? {})),
      ];
      for (const c of choices) if (c) set.add(c.exercises[0]!);
    }
  }
  return Array.from(set);
};

const musclesOf = (
  slugs: ReadonlyArray<string>,
  catalogBySlug: ReadonlyMap<string, Exercise>,
): string[] => {
  const set = new Set<string>();
  for (const slug of slugs) {
    for (const m of catalogBySlug.get(slug)?.primary ?? []) set.add(m);
  }
  return Array.from(set);
};

export const programMuscles = (
  sessions: ReadonlyArray<ProgramSession>,
  catalogBySlug: ReadonlyMap<string, Exercise>,
): string[] => musclesOf(programExerciseSlugs(sessions), catalogBySlug);

export const planMuscles = (
  entries: ReadonlyArray<PlannedExerciseEntry>,
  catalogBySlug: ReadonlyMap<string, Exercise>,
): string[] => musclesOf(planExerciseSlugs(entries), catalogBySlug);

export const modelMuscles = (
  model: Pick<ProgramModel, "days">,
  catalogBySlug: ReadonlyMap<string, Exercise>,
): string[] => musclesOf(modelExerciseSlugs(model), catalogBySlug);
